// frontend/mobile/src/pages/LoadingInput.tsx
// D5 (統合版): 積込場所入力 + 積込確認 (D5a廃止・1画面統合)
// ✅ D5積込場所入力 + D5a積込確認を1画面に統合
// ✅ 「運行開始」ボタンで直接 recordLoadingArrival API を呼び出し D4へ戻る
// ✅ D5a (LoadingConfirmation) は廃止 → /loading-confirmation は /loading-input へリダイレクト
// ✅ 複数品目選択対応
// ✅ e-reverse リンク: https://webpage.e-reverse.com
// 🔧 修正: D5a統合 (2025-03-08)

import React, { useState, useEffect } from 'react';
import { useTLog } from '../hooks/useTLog';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Truck,
  ChevronLeft,
  PlayCircle,
  AlertTriangle,
  CheckSquare
} from 'lucide-react';
import apiService, { retryWithBackoff } from '../services/api';
import { getBackgroundGPSState } from '../services/gpsBackgroundService';
import { useOperationStore } from '../stores/operationStore';

/** システム設定APIから大区分表示順を取得 */
async function fetchCategoryOrder(): Promise<('RECYCLED_MATERIAL' | 'VIRGIN_MATERIAL' | 'WASTE' | undefined)[]> {
  const DEFAULT: ('RECYCLED_MATERIAL' | 'VIRGIN_MATERIAL' | 'WASTE' | undefined)[] =
    ['RECYCLED_MATERIAL', 'VIRGIN_MATERIAL', 'WASTE', undefined];
  try {
    const apiBase = (window as any).__API_BASE_URL__
      || (import.meta as any).env?.VITE_API_BASE_URL
      || 'https://dump-tracker.ddns.net/api/v1';
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${apiBase}/settings/system`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return DEFAULT;
    const json = await res.json();
    const raw = json.data?.item_group_order;
    if (!raw) return DEFAULT;
    const order: string[] = JSON.parse(raw);
    // WASTE の後ろに undefined(その他) を追加
    const typed = order.filter(k =>
      ['RECYCLED_MATERIAL','VIRGIN_MATERIAL','WASTE'].includes(k)
    ) as ('RECYCLED_MATERIAL' | 'VIRGIN_MATERIAL' | 'WASTE')[];
    return [...typed, undefined];
  } catch { return DEFAULT; }
}

// ============================================================
// 型定義
// ============================================================

/** D4(OperationRecord)から渡される初期位置情報 */
interface LocationState {
  locationId: string;
  locationName: string;
  clientName: string;
  address?: string;
}

/** APIから取得する品目マスタ */
interface Item {
  id: string;
  name: string;
  itemType?: 'RECYCLED_MATERIAL' | 'VIRGIN_MATERIAL' | 'WASTE'; // REQ-009
  displayOrder?: number;
}

/** フォームデータ型 */
interface FormData {
  locationId: string;
  locationName: string;
  clientId: string;
  clientName: string;
  selectedItemIds: string[];
  selectedItemNames: string[];
  itemId: string;
  itemName: string;
  customItemName: string;
  cargoConfirmed: boolean;
  quantity?: number;
  notes?: string;
}

// ============================================================
// コンポーネント
// ============================================================

const LoadingInput: React.FC = () => {
  useTLog('LOADING_INPUT', '積載入力');

  const navigate = useNavigate();
  const location = useLocation();
  const operationStore = useOperationStore();
  const vehicleCapacity = operationStore.vehicleCapacity;  // REQ-004

  // D4から渡された位置情報（またはD5a廃止後のstate引き継ぎ）
  const locationState = location.state as LocationState | undefined;

  // REQ-003修正: 客先名は operationStore.customerName（運行開始時に選択した客先）を使用
  // 地点の contactPerson（担当者名）は客先名ではないため使用しない
  const resolvedClientName = operationStore.customerName || '';
  const resolvedClientId = operationStore.customerId || '';

  // REQ-020: 写真撮影
  const [cargoPhotoUrl, setCargoPhotoUrl] = useState<string | null>(null);
  const [cargoPhotoBlob, setCargoPhotoBlob] = useState<Blob | null>(null);
  const cargoInputRef = React.useRef<HTMLInputElement>(null);

  // ---- 客先切替ダイアログ ----
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [customerList, setCustomerList] = useState<{ id: string; name: string }[]>([]);
  const [isCustomerChanging, setIsCustomerChanging] = useState(false);
  // REQ-017: 新規客先登録フォーム
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);

  // ---- 品目マスタ ----
  const [items, setItems] = useState<Item[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  // 大項目グループ順序（CMSのシステム設定 item_group_order から取得）
  const [groupOrder, setGroupOrder] = useState<('RECYCLED_MATERIAL' | 'VIRGIN_MATERIAL' | 'WASTE' | undefined)[]>(
    ['RECYCLED_MATERIAL', 'VIRGIN_MATERIAL', 'WASTE', undefined]
  );

  // ---- フォームデータ ----
  const [formData, setFormData] = useState<FormData>({
    locationId: locationState?.locationId || '',
    locationName: locationState?.locationName || '',
    clientId: resolvedClientId,
    clientName: resolvedClientName,
    selectedItemIds: [],
    selectedItemNames: [],
    itemId: '',
    itemName: '',
    customItemName: '',
    cargoConfirmed: false,
    quantity: vehicleCapacity ?? undefined,  // REQ-004: 車両積載量をデフォルト値に
    notes: undefined,
  });

  // ---- 最終確認チェックボックス（D5a統合: "上記の内容で間違いありません"） ----
  const [finalConfirmed, setFinalConfirmed] = useState(false);

  // ---- 荷役作業トグル（2026-08吉原様案: 品目選択画面に配置。旧・独立ボタン式(要件No.17/18)は撤去） ----
  const [hasCargoWork, setHasCargoWork] = useState(false);

  // ---- 送信中フラグ ----
  const [isSubmitting, setIsSubmitting] = useState(false);
  // BUG-017: useRefで送信中フラグを管理（React state更新の非同期性による多重タップ防止）
  const isSubmittingRef = React.useRef(false);

  // ------------------------------------------------------------
  // 客先切替ハンドラー（D4から移植）
  // ------------------------------------------------------------
  const handleOpenCustomerDialog = async () => {
    try {
      const res = await apiService.getCustomers();
      const inner = res?.data?.customers ?? res?.data ?? res;
      setCustomerList(Array.isArray(inner) ? inner : []);
    } catch (e) {
      toast.error('客先一覧の取得に失敗しました');
    }
    setShowCustomerDialog(true);
  };

  const handleChangeCustomer = async (customerId: string, customerName: string) => {
    const currentOperationId = operationStore.operationId;
    if (!currentOperationId) {
      toast.error('運行IDが見つかりません');
      return;
    }
    setIsCustomerChanging(true);
    try {
      // BUG-019: retryWithBackoff追加（客先変更API）
      const res = await retryWithBackoff(
        () => apiService.changeOperationCustomer(currentOperationId, customerId),
        3, 1000, '客先変更'
      );
      if (res.success) {
        operationStore.setCustomerInfo({ customerId, customerName });
        setFormData(prev => ({ ...prev, clientId: customerId, clientName: customerName }));
        toast.success(`客先を「${customerName}」に変更しました`);
        setShowCustomerDialog(false);
        setShowNewCustomerForm(false);
      } else {
        toast.error(res.message || '客先の変更に失敗しました');
      }
    } catch (e) {
      toast.error('客先の変更に失敗しました');
    } finally {
      setIsCustomerChanging(false);
    }
  };

  // REQ-017: 新規客先登録ハンドラー
  const handleCreateCustomer = async () => {
    if (!newCustomerName.trim()) { toast.error('客先名を入力してください'); return; }
    setIsCreatingCustomer(true);
    try {
      const res = await (apiService as any).createCustomer({ name: newCustomerName.trim() });
      const created = res?.data ?? res;
      if (created?.id) {
        // 作成した客先を即選択
        await handleChangeCustomer(created.id, created.name);
        setCustomerList(prev => [...prev, { id: created.id, name: created.name }]);
        setNewCustomerName('');
        setShowNewCustomerForm(false);
        toast.success(`「${created.name}」を登録しました`);
      } else {
        toast.error('登録に失敗しました');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '客先登録に失敗しました');
    } finally {
      setIsCreatingCustomer(false);
    }
  };

  // ------------------------------------------------------------
  // 品目マスタ取得
  // ------------------------------------------------------------
  // API から大区分表示順を取得
  useEffect(() => {
    fetchCategoryOrder().then(setGroupOrder);
  }, []);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        setIsLoadingItems(true);
        const response = await apiService.getItems();
        if (response.success && response.data) {
          setItems(response.data);
          // groupOrder は fetchCategoryOrder() で取得済み
        }
      } catch (error) {
        console.error('品目取得エラー:', error);
        toast.error('品目の取得に失敗しました');
      } finally {
        setIsLoadingItems(false);
      }
    };
    fetchItems();
  }, []);

  // ------------------------------------------------------------
  // 位置情報がない場合は D4 に戻す
  // ------------------------------------------------------------
  useEffect(() => {
    if (!locationState?.locationId) {
      console.warn('⚠️ 積込場所情報がありません。D4に戻ります。');
      navigate('/operation-record', { replace: true });
    }
  }, [locationState, navigate]);

  // ============================================================
  // ハンドラー群
  // ============================================================

  /** 品目選択トグル（複数選択対応） */
  const handleItemToggle = (itemId: string) => {
    setFormData(prev => {
      const isSelected = prev.selectedItemIds.includes(itemId);
      const newSelection = isSelected
        ? prev.selectedItemIds.filter(id => id !== itemId)
        : [...prev.selectedItemIds, itemId];

      const selectedItems = items.filter(item => newSelection.includes(item.id));
      const firstItemId = newSelection.length > 0 && newSelection[0] ? newSelection[0] : '';
      const firstItemName = selectedItems.length > 0 && selectedItems[0] ? selectedItems[0].name : '';

      const itemName = items.find(i => i.id === itemId)?.name || itemId;
      console.log(`[D5-品目] ${isSelected ? '解除' : '選択'}: itemId=${itemId} name=${itemName} 選択後一覧=[${newSelection.map(id => items.find(i=>i.id===id)?.name||id).join(',')}]`);

      return {
        ...prev,
        selectedItemIds: newSelection,
        selectedItemNames: selectedItems.map(item => item.name),
        itemId: firstItemId,
        itemName: firstItemName,
      };
    });
  };

  /** 「その他」手入力ハンドラー */
  const handleCustomItemChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    console.log(`[D5-手入力品目] 入力変更: "${val}"`);
    setFormData(prev => ({ ...prev, customItemName: val }));
  };

  /** 数量入力ハンドラー */
  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    console.log(`[D5-数量] 入力変更: "${value}"`);
    setFormData(prev => ({
      ...prev,
      quantity: value ? parseFloat(value) : undefined,
    }));
  };

  /** 備考入力ハンドラー */
  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, notes: e.target.value }));
  };

  /** 戻るボタン → D4 運行中画面へ */
  const handleBack = () => {
    navigate('/operation-record');
  };

  // ============================================================
  // 「運行開始」ボタンハンドラー
  // ※ 旧D5の「進む」+ 旧D5aの「運行開始」を統合
  // ============================================================
  // REQ-020: カメラ/ファイル選択ハンドラー（Canvas圧縮付き）
  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // ✅ Canvas APIで送信前にリサイズ・圧縮（iPhoneの2〜3MB → 300KB以下）
    const MAX_WIDTH = 1280;
    const JPEG_QUALITY = 0.7;
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      // リサイズ比率計算（MAX_WIDTH以下ならそのまま）
      const ratio = img.width > MAX_WIDTH ? MAX_WIDTH / img.width : 1;
      const width  = Math.round(img.width  * ratio);
      const height = Math.round(img.height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width  = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob) return;
          console.log(`📷 写真圧縮: ${(file.size / 1024).toFixed(0)}KB → ${(blob.size / 1024).toFixed(0)}KB`);
          setCargoPhotoBlob(blob);
          setCargoPhotoUrl(URL.createObjectURL(blob));
          toast.success('写真を選択しました');
        },
        'image/jpeg',
        JPEG_QUALITY
      );
    };
    img.src = objectUrl;
  };

  const handleStartOperation = async () => {
    // ---- バリデーション ----
    if (
      formData.selectedItemIds.length === 0 &&
      !formData.itemId &&
      !formData.customItemName
    ) {
      toast.error('品目を選択するか、「その他」に入力してください');
      return;
    }

    if (!finalConfirmed) {
      toast.error('「上記の内容で間違いありません」にチェックを入れてください');
      return;
    }

    // ---- 運行ID確認 ----
    const currentOperationId = operationStore.operationId;
    if (!currentOperationId) {
      toast.error('運行IDが見つかりません。運行を開始してください。', { duration: 5000 });
      console.error('❌ 運行ID未設定:', { operationStoreId: operationStore.operationId });
      navigate('/operation-record', { replace: true });
      return;
    }

    // ---- GPS確認 ----
    if (!navigator.geolocation) {
      toast.error('GPS機能が利用できません');
      return;
    }

    try {
      // BUG-017: useRefによる確実な二重送信防止
    if (isSubmittingRef.current) {
      console.warn('[LoadingInput] ⚠️ BUG-017: 送信中のため多重タップを無視');
      return;
    }
    isSubmittingRef.current = true;
    setIsSubmitting(true);

      // GPS位置取得: バックグラウンドGPSの最新座標を使用
      // gpsBackgroundServiceが既に3秒ごとに取得・保持しているため再取得不要
      const bgState = getBackgroundGPSState();
      let position: GeolocationPosition;
      if (bgState.lastPosition) {
        // ✅ 通常ケース: バックグラウンドサービスの取得済み最新座標を使用
        position = bgState.lastPosition;
        console.log('[LoadingInput] ✅ BGサービス座標使用:', {
          lat: position.coords.latitude, lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
      } else {
        // ⚠️ フォールトトレラント: BGサービス未起動時のみ直接取得
        console.warn('[LoadingInput] BGサービス座標なし → 直接取得');
        position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: false,
            timeout: 10000,
            maximumAge: 60000,
          });
        });
      }

      console.log('🚛 積込場所到着記録API呼び出し開始');
      const _lp: number = Number((operationStore as any).loadingPattern ?? 2);
      console.log('[D5-送信] 積込確認ボタン押下 (loadingPattern=' + _lp + '):', JSON.stringify({
        operationId: currentOperationId,
        locationId: formData.locationId,
        locationName: formData.locationName,
        clientName: formData.clientName,
        selectedItemIds: formData.selectedItemIds,
        selectedItemNames: formData.selectedItemNames,
        itemId: formData.itemId,
        itemName: formData.itemName,
        customItemName: formData.customItemName,
        quantity: formData.quantity,
        notes: formData.notes,
        cargoConfirmed: formData.cargoConfirmed,
        gps: { lat: position.coords.latitude, lng: position.coords.longitude, accuracy: position.coords.accuracy },
      }));

      // 品目IDの決定
      const itemId = formData.itemId || undefined;

      // ✅ 複数品目は selectedItemIds で送信（notes 埋め込み廃止）
      // P1: recordLoadingArrival を呼ばず場所・品目をstoreに保存のみ
      //     → 積込開始ボタン(handleLoadingStart)でstartLoading APIを呼ぶ
      // P2: recordLoadingArrival → actualStartTime=今, actualEndTime=null のレコード作成
      //     → 積込完了ボタン(handleLoadingComplete)でcompleteLoading APIを呼ぶ
      // P3: recordLoadingArrival(endTime=now) → actualStartTime=actualEndTime=今 → 即完了
      const _lpNow: number = Number((operationStore as any).loadingPattern ?? 2);
      const _nowTime = new Date();

      // ✅ FIX-P1: P1の場合はAPIを呼ばず、場所・品目情報をstoreに保存して遷移
      if (_lpNow === 1) {
        console.log('[D5-P1] P1パターン: recordLoadingArrival をスキップ。場所・品目をstoreに保存。');
        // 場所・品目をstoreに保存（handleLoadingStartで使用）
        operationStore.setLoadingLocation(formData.locationName, formData.locationId);
        (operationStore as any).loadingItemId = itemId || undefined;
        (operationStore as any).loadingCustomerId = formData.clientId || undefined;
        (operationStore as any).loadingCustomItemName = formData.customItemName || undefined;
        (operationStore as any).loadingSelectedItemIds = formData.selectedItemIds.length > 0 ? formData.selectedItemIds : undefined;
        (operationStore as any).loadingQuantity = formData.quantity;
        (operationStore as any).loadingNotes = formData.notes || undefined;
        (operationStore as any).loadingLocationLat = position.coords.latitude;
        (operationStore as any).loadingLocationLng = position.coords.longitude;
        (operationStore as any).loadingLocationAccuracy = position.coords.accuracy;
        // 🆕 荷役作業トグル（P1: 積込開始ボタン押下時にstartLoadingへ渡すためstoreに保存）
        (operationStore as any).loadingHasCargoWork = hasCargoWork;
        // フェーズ更新してAT_LOADINGへ
        operationStore.setPhase('AT_LOADING');
        apiService.logOperationEvent({
          eventType: 'LOADING_ARRIVED',
          operationId: currentOperationId,
          locationId: formData.locationId,
          locationName: formData.locationName,
          phase: 'AT_LOADING',
          result: 'success',
        }).catch(() => {});
        toast.success('積込場所に到着しました。積込開始ボタンを押してください。', { duration: 3000, icon: '🚛' });
        navigate('/operation-record', { replace: true });
        return;
      }

      // P2/P3: recordLoadingArrival を呼ぶ
      const response = await apiService.recordLoadingArrival(currentOperationId, {
        locationId: formData.locationId,
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        arrivalTime: _nowTime,
        itemId: itemId,
        customerId: formData.clientId || undefined,  // ✅ この積込だけの独立した客先
        selectedItemIds: formData.selectedItemIds.length > 0 ? formData.selectedItemIds : undefined,
        customItemName: formData.customItemName || undefined,  // ✅ 手入力品目名
        quantity: formData.quantity,
        notes: formData.notes || undefined,
        hasCargoWork,  // 🆕 荷役作業トグル（P2/P3パターン）
        // P3: endTime を同時送信 → actualStartTime = actualEndTime
        ...(_lpNow === 3 ? { endTime: _nowTime } : {}),
      });

      console.log('✅ 積込場所到着記録完了');
      console.log('📦 API応答:', response);
      // ✅ 修正: 品目情報を operationStore に保存（積込完了ボタン押下時に使用）
      if (formData.itemId || formData.customItemName) {
        (operationStore as any).loadingItemId = formData.itemId || undefined;
        (operationStore as any).loadingCustomItemName = formData.customItemName || undefined;
        console.log('[D5] 品目情報をoperationStoreに保存:', { itemId: formData.itemId, customItemName: formData.customItemName });
      }

      // REQ-020: 写真が選択されていればアップロード
      // recordLoadingArrival戻り値: APIResponse<ActivityRecordResponse> = { success, data: { id, ... }, message }
      // axiosInstance.post → return response.data → { success, data: { id }, message }
      // よって response.data.id が operationDetail の実際のID
      const _r: any = response;
      const _detailId: string | null =
        _r?.data?.id ??        // 正常ケース
        _r?.data?.data?.id ??  // BUG-013二重ネスト
        _r?.id ??              // 直接id
        null;
      console.log('📷 [REQ-020] detailId取得:', {
        detailId: _detailId,
        hasPhoto: !!cargoPhotoBlob,
        responseKeys: Object.keys(_r || {}),
        dataId: _r?.data?.id,
        blobSize: cargoPhotoBlob?.size,
      });
      if (cargoPhotoBlob && _detailId) {
        try {
          await (apiService as any).uploadOperationDetailImage(
            _detailId,
            cargoPhotoBlob,
            `cargo_${Date.now()}.jpg`
          );
          console.log('✅ REQ-020 写真アップロード完了');
          toast.success('積込記録と写真を登録しました', { duration: 3000, icon: '📷' });
        } catch (photoErr: any) {
          console.error('写真アップロード失敗詳細:', {
            detailId: _detailId,
            status: photoErr?.response?.status,
            serverMsg: photoErr?.response?.data?.message,
            errorMsg: photoErr?.message,
          });
          toast('写真のアップロードに失敗しましたが運行は記録されました', { icon: '⚠️' });
        }
      } else if (cargoPhotoBlob && !_detailId) {
        console.error('❌ [REQ-020] detailIdがnullのため写真アップロードをスキップ:', { response: _r });
        toast('写真のアップロードに失敗しましたが運行は記録されました', { icon: '⚠️' });
      }

      // P3(即時完了)の場合は積込完了済みなのでTO_UNLOADINGへ、それ以外はAT_LOADINGに留まる
      const _lpAfter: number = Number(operationStore.loadingPattern ?? 2);
      if (_lpAfter === 3) {
        console.log('🔄 [P3] 即時完了: フェーズ → TO_UNLOADING');
        operationStore.setPhase('TO_UNLOADING');
      } else {
        console.log('🔄 フェーズ維持: AT_LOADING（積込確認完了・自動移動検知待ち）');
        operationStore.setPhase('AT_LOADING');
      }
      operationStore.setLoadingLocation(formData.locationName, formData.locationId);
      // 🚛 運行イベントログ（積込到着）
      apiService.logOperationEvent({
        eventType: 'LOADING_ARRIVED',
        operationId: currentOperationId,
        locationId: formData.locationId,
        locationName: formData.locationName,
        itemId: formData.itemId || undefined,
        itemName: formData.itemName || undefined,
        customItemName: formData.customItemName || undefined,
        quantity: formData.quantity,
        gps: position ? { lat: position.coords.latitude, lng: position.coords.longitude, accuracy: position.coords.accuracy } : undefined,
        phase: 'AT_LOADING',
        result: 'success',
      }).catch(() => {});

      // 少し待機して状態更新を確実にする
      await new Promise(resolve => setTimeout(resolve, 500));

      toast.success('積み込みを確認しました。出発すると自動で「移動中」に切り替わります。', {
        duration: 4000,
        icon: '🚛',
      });

      // D4 運行中画面へ戻る
      navigate('/operation-record', { replace: true });

    } catch (error: any) {
      console.error('❌ 積込場所到着記録エラー:', error);
      if (error.name === 'GeolocationPositionError') {
        toast.error('GPS位置情報の取得に失敗しました。位置情報を有効にしてください。', {
          duration: 5000,
        });
      } else {
        toast.error(error.message || '積込記録の登録に失敗しました', { duration: 5000 });
      }
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  // ============================================================
  // 表示用の計算値
  // ============================================================
  const hasItemSelected =
    formData.selectedItemIds.length > 0 ||
    !!formData.itemId ||
    !!formData.customItemName;

  const isStartButtonEnabled = hasItemSelected && finalConfirmed && !isSubmitting;

  // ⑤ 運行パターンにより表示文言を出し分け: P3=「運行開始」 / P1,P2=「積込開始」
  const _startBtnLoadingPattern = Number((operationStore as any).loadingPattern ?? 2);
  const startButtonLabel = _startBtnLoadingPattern === 3 ? '運行開始' : '積込開始';

  const displayItemLabels =
    formData.selectedItemNames.length > 0
      ? formData.selectedItemNames.join('、')
      : formData.itemName || formData.customItemName || '';

  // ============================================================
  // レンダリング
  // ============================================================
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        background: '#f5f5f5',
      }}
    >
      {/* ===== ヘッダー ===== */}
      <header
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Truck style={{ width: '28px', height: '28px' }} />
          <div>
            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>
              客先積載物入力
            </h1>
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', opacity: 0.9 }}>
              品目と積み込み量を確認してください
            </p>
          </div>
        </div>
      </header>

      {/* ===== メインコンテンツ ===== */}
      <main style={{ flex: 1, padding: '16px', maxWidth: '600px', margin: '0 auto', width: '100%' }}>

        {/* ----- 積込場所セクション ----- */}
        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '16px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          }}
        >
          <h2
            style={{
              margin: '0 0 12px 0',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span style={{ fontSize: '18px' }}>📍</span>
            積込場所
          </h2>

          <div style={{ marginBottom: '8px' }}>
            <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>
              客先名
            </label>
            <button
              onClick={handleOpenCustomerDialog}
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: '#f0f4ff',
                borderRadius: '8px',
                border: '2px solid #667eea',
                fontSize: '15px',
                color: '#374151',
                textAlign: 'left',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                boxSizing: 'border-box',
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                🏢 {formData.clientName || '（タップして客先を選択）'}
              </span>
              <span style={{ fontSize: '18px', flexShrink: 0 }}>🔄</span>
            </button>
          </div>

          <div>
            <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>
              場所
            </label>
            <div
              style={{
                padding: '10px 12px',
                background: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                fontSize: '15px',
                color: '#374151',
              }}
            >
              {formData.locationName || '場所未設定'}
            </div>
          </div>
        </div>

        {/* ----- 品目選択セクション ----- */}
        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '16px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          }}
        >
          <h2
            style={{
              margin: '0 0 4px 0',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span style={{ fontSize: '18px' }}>📦</span>
            品目選択
          </h2>
          <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#6b7280' }}>
            品目を選択＊（複数選択可能）
          </p>

          {/* 🆕 荷役作業トグル（2026-08吉原様案）: この積込に荷役作業が伴う場合はON */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 12px',
              marginBottom: '14px',
              background: hasCargoWork ? '#eff6ff' : '#f9fafb',
              border: `1px solid ${hasCargoWork ? '#93c5fd' : '#e5e7eb'}`,
              borderRadius: '8px',
            }}
          >
            <div>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>
                荷役作業
              </p>
              <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: '#6b7280' }}>
                この積込に荷役（積込の実作業）が伴う場合はONにしてください
              </p>
            </div>
            <button
              type="button"
              onClick={() => setHasCargoWork(v => !v)}
              aria-pressed={hasCargoWork}
              style={{
                position: 'relative',
                width: '44px',
                height: '24px',
                borderRadius: '12px',
                border: 'none',
                cursor: 'pointer',
                background: hasCargoWork ? '#2563eb' : '#d1d5db',
                transition: 'background 0.2s',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: '2px',
                  left: hasCargoWork ? '22px' : '2px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: 'white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  transition: 'left 0.2s',
                }}
              />
            </button>
          </div>

          {/* REQ-009: 品目区分ごとにグループ表示 */}
          {isLoadingItems ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
              品目を読み込み中...
            </div>
          ) : (() => {
            // 区分定義（CMSの品目displayOrderから動的に決定）
            const TYPE_LABEL_MAP: Record<string, string> = {
              RECYCLED_MATERIAL: '再生材',
              VIRGIN_MATERIAL: 'バージン材',
              WASTE: '廃棄物',
            };
            const TYPE_GROUPS: { key: 'RECYCLED_MATERIAL' | 'VIRGIN_MATERIAL' | 'WASTE' | undefined; label: string }[] =
              groupOrder.map(k => ({
                key: k,
                label: k ? (TYPE_LABEL_MAP[k] ?? k) : 'その他',
              }));
            // 区分ごとにグループ化してソート
            const grouped = TYPE_GROUPS.map(group => ({
              ...group,
              items: items
                .filter(it => it.itemType === group.key || (group.key === undefined && !it.itemType))
                .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999)),
            })).filter(g => g.items.length > 0);

            return (
              <div style={{ marginBottom: '12px' }}>
                {grouped.map(group => (
                  <React.Fragment key={group.label}>
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{
                        fontSize: '11px',
                        fontWeight: 'bold',
                        color: '#9ca3af',
                        letterSpacing: '0.05em',
                        marginBottom: '6px',
                        paddingBottom: '4px',
                        borderBottom: '1px solid #f3f4f6',
                      }}>
                        {group.label}
                      </div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '8px',
                      }}>
                        {group.items.map(item => {
                          const isSelected = formData.selectedItemIds.includes(item.id);
                          return (
                            <button
                              key={item.id}
                              onClick={() => handleItemToggle(item.id)}
                              style={{
                                padding: '10px 6px',
                                fontSize: '14px',
                                fontWeight: isSelected ? 'bold' : 'normal',
                                color: isSelected ? 'white' : '#374151',
                                background: isSelected
                                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                  : 'white',
                                border: `2px solid ${isSelected ? '#667eea' : '#d1d5db'}`,
                                borderRadius: '8px',
                                cursor: 'pointer',
                                textAlign: 'center',
                              }}
                            >
                                  {isSelected ? `✓ ${item.name}` : item.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {/* 廃棄物グループの直下に電子マニフェストリンクを表示 */}
                    {group.key === 'WASTE' && (
                      <div style={{
                        margin: '4px 0 16px 0',
                        padding: '14px 16px',
                        background: '#fffbeb',
                        border: '2px solid #f59e0b',
                        borderRadius: '10px',
                      }}>
                        <p style={{
                          margin: 0,
                          fontSize: '15px',
                          color: '#78350f',
                          lineHeight: '1.7',
                          fontWeight: '600',
                        }}>
                          📋 産業廃棄物マニフェストを登録する場合は、
                          <a
                            href="https://webpage.e-reverse.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#1d4ed8',
                              textDecoration: 'underline',
                              fontWeight: '700',
                              fontSize: '16px',
                            }}
                          >
                            こちら
                          </a>
                          からログインしてください。
                        </p>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            );
          })()}

          {/* 選択中表示 */}
          {displayItemLabels && (
            <p
              style={{
                margin: '8px 0 12px 0',
                fontSize: '13px',
                color: '#667eea',
                fontWeight: '500',
              }}
            >
              選択中: {displayItemLabels}
            </p>
          )}

          {/* その他（手入力） */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>
              その他（手入力）
            </label>
            <input
              type="text"
              value={formData.customItemName}
              onChange={handleCustomItemChange}
              placeholder="上記にない品目を入力"
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '15px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#9ca3af' }}>
              ※ 上記のリストにない品目を入力できます
            </p>
          </div>

          {/* 数量 */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>
              数量（トン）
            </label>
            <input
              type="number"
              value={formData.quantity !== undefined ? formData.quantity : ''}
              onChange={handleQuantityChange}
              placeholder="例: 10.5"
              step="0.1"
              min="0"
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '15px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* 備考 */}
          <div>
            <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>
              備考
            </label>
            <textarea
              value={formData.notes || ''}
              onChange={handleNotesChange}
              placeholder="特記事項があれば入力"
              rows={3}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '15px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                outline: 'none',
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        {/* ----- 最終確認チェックボックス（D5a統合）----- */}
        <div
          style={{
            background: finalConfirmed ? '#d1fae5' : '#f9fafb',
            border: `2px solid ${finalConfirmed ? '#10b981' : '#d1d5db'}`,
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '16px',
            transition: 'all 0.2s ease',
          }}
        >
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={finalConfirmed}
              onChange={e => {
                setFinalConfirmed(e.target.checked);
              }}
              style={{
                width: '22px',
                height: '22px',
                marginTop: '2px',
                cursor: 'pointer',
                flexShrink: 0,
                accentColor: '#10b981',
              }}
            />
            <div>
              <span
                style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: finalConfirmed ? '#065f46' : '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {finalConfirmed && (
                  <CheckSquare style={{ width: '18px', height: '18px', color: '#10b981' }} />
                )}
                上記の内容で間違いありません
              </span>
              <p
                style={{
                  margin: '4px 0 0 0',
                  fontSize: '13px',
                  color: finalConfirmed ? '#047857' : '#6b7280',
                }}
              >
                積み込みの確認が完了しました
              </p>
            </div>
          </label>
        </div>

        {/* ----- 警告・注意事項（D5a統合・文字拡大）----- */}
        <div
          style={{
            background: '#fffbeb',
            border: '2px solid #f59e0b',
            borderRadius: '12px',
            padding: '18px 16px',
            marginBottom: '16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '10px' }}>
            <AlertTriangle
              style={{
                width: '24px',
                height: '24px',
                color: '#d97706',
                flexShrink: 0,
                marginTop: '2px',
              }}
            />
            <p
              style={{
                margin: 0,
                fontSize: '15px',
                fontWeight: '700',
                color: '#92400e',
                lineHeight: '1.6',
              }}
            >
               注意: 「{startButtonLabel}」ボタンを押すと、GPS位置と共に積込記録が登録され、積降場所への移動フェーズに移行します。
            </p>
          </div>
          {/* マニフェストリンクは廃棄物グループ直下に移動 */}
        </div>

      </main>

      {/* ===== 客先切替ダイアログ（D4から移植） ===== */}
      {showCustomerDialog && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px'
        }}>
          <div style={{
            background: 'white', borderRadius: '12px', padding: '20px',
            width: '100%', maxWidth: '360px', maxHeight: '80vh', overflow: 'auto'
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: 'bold', color: '#333' }}>
              🔄 別客先へ切替
            </h3>
            <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#666' }}>
              変更する客先を選択してください
            </p>
            {customerList.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                客先が登録されていません
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {customerList.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => handleChangeCustomer(c.id, c.name)}
                    disabled={isCustomerChanging}
                    style={{
                      padding: '12px 16px',
                      fontSize: '15px',
                      textAlign: 'left',
                      background: isCustomerChanging ? '#f5f5f5' : '#f8f9fa',
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      cursor: isCustomerChanging ? 'not-allowed' : 'pointer',
                      color: '#333'
                    }}
                  >
                    🏢 {c.name}
                  </button>
                ))}
              </div>
            )}
            {/* REQ-017: 新規客先登録フォーム */}
            {showNewCustomerForm ? (
              <div style={{ marginTop: '12px', borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}>
                <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 'bold', color: '#374151' }}>＋ 新規客先を登録</p>
                <input
                  type="text"
                  value={newCustomerName}
                  onChange={e => setNewCustomerName(e.target.value)}
                  placeholder="客先名を入力"
                  style={{
                    width: '100%', padding: '10px', fontSize: '15px',
                    border: '1.5px solid #3b82f6', borderRadius: '8px',
                    boxSizing: 'border-box', marginBottom: '8px'
                  }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleCreateCustomer}
                    disabled={isCreatingCustomer || !newCustomerName.trim()}
                    style={{
                      flex: 1, padding: '10px', fontSize: '14px',
                      background: isCreatingCustomer ? '#93c5fd' : '#3b82f6',
                      color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
                    }}
                  >
                    {isCreatingCustomer ? '登録中...' : '登録して選択'}
                  </button>
                  <button
                    onClick={() => { setShowNewCustomerForm(false); setNewCustomerName(''); }}
                    style={{
                      padding: '10px 16px', fontSize: '14px',
                      background: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer'
                    }}
                  >
                    戻る
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowNewCustomerForm(true)}
                style={{
                  marginTop: '12px', width: '100%', padding: '10px',
                  fontSize: '14px', background: '#f0f9ff',
                  border: '1.5px dashed #3b82f6', borderRadius: '8px',
                  cursor: 'pointer', color: '#2563eb', fontWeight: 'bold'
                }}
              >
                ＋ 新規客先を追加
              </button>
            )}
            <button
              onClick={() => { setShowCustomerDialog(false); setShowNewCustomerForm(false); setNewCustomerName(''); }}
              style={{
                marginTop: '16px', width: '100%', padding: '12px',
                fontSize: '15px', background: '#e0e0e0', border: 'none',
                borderRadius: '8px', cursor: 'pointer', color: '#333'
              }}
            >
              キャンセル
            </button>
          </div>
        </div>
      )}

      {/* REQ-020: 積載物写真撮影セクション */}
      <div style={{
        padding: '12px 16px',
        borderTop: '1px solid #e5e7eb',
        background: '#f9fafb'
      }}>
        <p style={{ margin: '0 0 8px', fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>📷 積載物写真（任意）</p>
        {cargoPhotoUrl ? (
          <div style={{ position: 'relative', marginBottom: '8px' }}>
            <img
              src={cargoPhotoUrl}
              alt="積載物"
              style={{ width: '100%', maxHeight: '160px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #10b981' }}
            />
            <button
              onClick={() => { setCargoPhotoUrl(null); setCargoPhotoBlob(null); }}
              style={{
                position: 'absolute', top: '6px', right: '6px',
                background: 'rgba(0,0,0,0.6)', color: 'white',
                border: 'none', borderRadius: '50%', width: '28px', height: '28px',
                fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >✕</button>
          </div>
        ) : null}
        <input
          ref={cargoInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handlePhotoChange}
          style={{ display: 'none' }}
        />
        <button
          onClick={() => cargoInputRef.current?.click()}
          style={{
            width: '100%', padding: '10px',
            fontSize: '14px', fontWeight: 'bold',
            background: cargoPhotoUrl ? '#f0fdf4' : '#fff',
            border: `1.5px solid ${cargoPhotoUrl ? '#10b981' : '#d1d5db'}`,
            borderRadius: '8px', cursor: 'pointer',
            color: cargoPhotoUrl ? '#065f46' : '#6b7280'
          }}
        >
          {cargoPhotoUrl ? '📷 写真を撮り直す' : '📷 写真を撮影する'}
        </button>
      </div>

      {/* ===== フッター（ボタン）===== */}
      <footer
        style={{
          background: 'white',
          padding: '16px 20px',
          borderTop: '1px solid #e5e7eb',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.05)',
          position: 'sticky',
          bottom: 0,
        }}
      >
        <div
          style={{
            maxWidth: '600px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: '1fr 1.5fr',
            gap: '12px',
          }}
        >
          {/* 戻るボタン */}
          <button
            onClick={handleBack}
            disabled={isSubmitting}
            style={{
              padding: '14px',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#374151',
              background: 'white',
              border: '2px solid #d1d5db',
              borderRadius: '8px',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              opacity: isSubmitting ? 0.6 : 1,
            }}
          >
            <ChevronLeft style={{ width: '20px', height: '20px' }} />
            戻る
          </button>

          {/* 運行開始ボタン */}
          <button
            onClick={handleStartOperation}
            disabled={!isStartButtonEnabled}
            style={{
              padding: '14px',
              fontSize: '16px',
              fontWeight: 'bold',
              color: 'white',
              background: isStartButtonEnabled
                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                : '#d1d5db',
              border: 'none',
              borderRadius: '8px',
              cursor: isStartButtonEnabled ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: isStartButtonEnabled ? '0 2px 8px rgba(16,185,129,0.3)' : 'none',
              transition: 'all 0.2s ease',
            }}
          >
            {isSubmitting ? (
              <>処理中...</>
            ) : (
              <>
                <PlayCircle style={{ width: '20px', height: '20px' }} />
                {startButtonLabel}
              </>
            )}
          </button>
        </div>
      </footer>
    </div>
  );
};

export default LoadingInput;